import {
  interpolateCividis,
  interpolateInferno,
  interpolateMagma,
  interpolatePlasma,
  interpolateRdBu,
  interpolateRdYlBu,
  interpolateTurbo,
  interpolateViridis,
} from 'd3-scale-chromatic';

export const colormaps = {
  'plasma': interpolatePlasma,
  'viridis': interpolateViridis,
  'turbo': interpolateTurbo,
  'magma': interpolateMagma,
  'inferno': interpolateInferno,
  'cividis': interpolateCividis,
  'red-yellow-blue': interpolateRdYlBu,
  'red-blue': interpolateRdBu,
} as const;

export type ColormapName = keyof typeof colormaps;

// null / undefined / NaN are "no data"; 0 is a real value.
const isMissing = (v: number | null | undefined): boolean =>
  v === null || v === undefined || Number.isNaN(v);

function meanStd(values: number[]): { mean: number; std: number } {
  const valid = values.filter(v => !isMissing(v));
  if (!valid.length) return { mean: 0, std: 0 };
  const mean = valid.reduce((a, b) => a + b, 0) / valid.length;
  const variance
    = valid.reduce((a, b) => a + (b - mean) ** 2, 0) / valid.length;
  return { mean, std: Math.sqrt(variance) };
}

/**
 * Maps a numeric value to a color using a d3 colormap.
 *
 * By default the domain is `mean ± 2·std` over `values` (identical to the
 * Polarmap normalization); pass an explicit `domain` to override. Returns
 * `null` for missing values so callers can fall back to a default color.
 *
 * Generic over any metric (wall thickening, fractional shortening, etc.).
 */
export function createColorScale(
  values: number[],
  colormap: ColormapName = 'plasma',
  domain?: [number, number],
  invert = false,
): (value: number | null | undefined) => string | null {
  let min: number;
  let max: number;
  if (domain) {
    [min, max] = domain;
  }
  else {
    const { mean, std } = meanStd(values);
    min = mean - 2 * std;
    max = mean + 2 * std;
  }
  const span = max - min;
  const interp = colormaps[colormap];
  return (value) => {
    if (isMissing(value)) return null;
    const t = span === 0 ? 0.5 : (value! - min) / span;
    const tc = Math.max(0, Math.min(1, t));
    return interp(invert ? 1 - tc : tc);
  };
}

/**
 * Converts an RGBA tuple to a hex string with alpha.
 *
 * Adds the prefix '#'.
 *
 * @param rgba a 4-tuple with components ranging from 0-255.
 */
export function rgbaToHexa(rgba: number[]) {
  const hexa = rgba.map(comp => `0${comp.toString(16)}`.slice(-2));
  return `#${hexa.join('')}`;
}

/**
 * Parses a hex color with optional alpha channel.
 *
 * Returns an RGBA array with components scaled to [0,255].
 *
 * @param hexa a hexa color in the format "[#]RRGGBB[AA]"
 */
export function hexaToRGBA(hexa: string) {
  const values = hexa.startsWith('#') ? hexa.substring(1) : hexa;
  const rgba = [0, 0, 0, 255];
  const length = Math.min(4, values.length / 2);
  for (let i = 0; i < length; i++) {
    rgba[i] = Number.parseInt(values.substring(2 * i, 2 * i + 2), 16);
  }
  return rgba;
}
